import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { GoogleLogin } from "@react-oauth/google";
import "./Auth.css";
import { useTranslation } from "react-i18next";
import "../i18n";

const Register = () => {
  const { t } = useTranslation();
  const { register, googleLogin } = useAuth();
  const navigate = useNavigate();

  const [form, setForm]       = useState({ username: "", email: "", password: "", confirm: "" });
  const [error, setError]     = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw]   = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setTimeout(() => setMounted(true), 50); }, []);
  const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault(); setError("");
    if (form.password !== form.confirm) { setError("Passwords do not match."); return; }
    if (form.password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setLoading(true);
    try { await register(form.username, form.email, form.password); navigate("/", { replace: true }); }
    catch (err) { setError(err.message || "Registration failed. Please try again."); }
    finally { setLoading(false); }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError(""); setLoading(true);
    try { await googleLogin(credentialResponse.credential); navigate("/", { replace: true }); }
    catch (err) { setError(err.message || "Google sign-in failed. Please try again."); }
    finally { setLoading(false); }
  };

  const handleGoogleError = () => { setError("Google sign-in was cancelled or failed. Please try again."); };

  const getStrength = (pw) => {
    if (!pw) return { level: 0, label: "", color: "transparent" };
    if (pw.length < 6) return { level: 1, label: "Weak", color: "#ef4444" };
    if (pw.length < 10) return { level: 2, label: "Fair", color: "#f59e0b" };
    if (/[A-Z]/.test(pw) && /[0-9]/.test(pw)) return { level: 4, label: "Strong", color: "#10b981" };
    return { level: 3, label: "Good", color: "#0a9b7c" };
  };
  const strength = getStrength(form.password);

  return (
    <div className={`auth-page ${mounted ? "auth-page--in" : ""}`}>
      <div className="auth-bg">
        <div className="auth-orb auth-orb--1"></div><div className="auth-orb auth-orb--2"></div>
        <div className="auth-orb auth-orb--3"></div><div className="auth-orb auth-orb--4"></div>
        <div className="auth-noise"></div><div className="auth-grid"></div>
      </div>
      <div className="auth-container">
        <div className="auth-hero">
          <div className="auth-hero-inner">
            <Link to="/" style={{ textDecoration: "none" }}>
              <div className="auth-hero-logo"><img src={`${process.env.PUBLIC_URL}/SolviotechFavicon.ico`} alt="Solviotech" style={{ height: "40px", width: "40px", objectFit: "contain", borderRadius: "8px" }} /><div className="auth-hero-logo-text">Solvio<strong>tech</strong></div></div>
            </Link>
            <Link to="/" className="auth-back-link"><i className="fa-solid fa-arrow-left"></i> {t("auth.backToSite")}</Link>
            <div className="auth-hero-tag"><span className="auth-hero-tag-dot"></span>Join the team building Africa's digital future</div>
            <h1 className="auth-hero-heading">Start building<br /><em className="auth-hero-em">with us today</em></h1>
            <p className="auth-hero-desc">Create your account and get full access to the Solviotech platform — once approved by an admin.</p>
            <div className="auth-hero-perks">
              {[
                { icon: "fa-solid fa-gauge-high",   title: "Full Dashboard Access", desc: "Manage all platform content" },
                { icon: "fa-solid fa-users",         title: "Team Collaboration",   desc: "Work alongside the Solviotech team" },
                { icon: "fa-solid fa-shield-halved", title: "Role-Based Security",  desc: "Admin approval required for access" },
                { icon: "fa-solid fa-bolt",          title: "Real-time Updates",    desc: "Changes reflect instantly on the site" },
              ].map((p) => (
                <div key={p.title} className="auth-perk">
                  <div className="auth-perk-icon"><i className={p.icon}></i></div>
                  <div className="auth-perk-text"><span className="auth-perk-title">{p.title}</span><span className="auth-perk-desc">{p.desc}</span></div>
                </div>
              ))}
            </div>
            <div className="auth-hero-note">
              <i className="fa-solid fa-circle-info"></i>
              New accounts have <strong>user</strong> access by default. An existing admin must upgrade your role to unlock the dashboard.
            </div>
          </div>
        </div>

        <div className="auth-panel">
          <div className="auth-card">
            <div className="auth-card-top">
            <img src={`${process.env.PUBLIC_URL}/SolviotechFavicon.ico`} alt="Solviotech" style={{ height: "36px", width: "36px", objectFit: "contain", borderRadius: "8px" }} />
              <div>
                <h2 className="auth-card-title">{t("auth.createAccount")}</h2>
                <p className="auth-card-sub">{t("auth.joinSub")}</p>
              </div>
            </div>
            {error && <div className="auth-alert"><i className="fa-solid fa-circle-exclamation"></i> {error}</div>}
            <form onSubmit={handleSubmit} noValidate className="auth-form-body">
              <div className="auth-field">
                <label className="auth-label">{t("auth.workspace")}</label>
                <div className="auth-input-wrap">
                  <i className="fa-solid fa-fingerprint auth-input-icon"></i>
                  <input type="text" name="username" className="auth-input" placeholder={t("auth.workspacePlaceholder")} value={form.username} onChange={handleChange} required />
                </div>
              </div>
              <div className="auth-field">
                <label className="auth-label">{t("auth.workEmail")}</label>
                <div className="auth-input-wrap">
                  <i className="fa-solid fa-at auth-input-icon"></i>
                  <input type="email" name="email" className="auth-input" placeholder={t("auth.workEmailPlaceholder")} value={form.email} onChange={handleChange} required />
                </div>
              </div>
              <div className="auth-field">
                <label className="auth-label">{t("auth.createPassword")}</label>
                <div className="auth-input-wrap">
                  <i className="fa-solid fa-key auth-input-icon"></i>
                  <input type={showPw ? "text" : "password"} name="password" className="auth-input" placeholder={t("auth.createPasswordPlaceholder")} value={form.password} onChange={handleChange} required />
                  <button type="button" className="auth-eye-btn" onClick={() => setShowPw(v => !v)}>
                    <i className={`fa-solid ${showPw ? "fa-eye-slash" : "fa-eye"}`}></i>
                  </button>
                </div>
                {form.password && (
                  <div className="auth-strength">
                    <div className="auth-strength-track">
                      {[1,2,3,4].map((i) => <div key={i} className="auth-strength-seg" style={{ background: i <= strength.level ? strength.color : "rgba(255,255,255,0.08)" }} />)}
                    </div>
                    <span style={{ color: strength.color, fontSize: "0.72rem", fontWeight: 600 }}>{strength.label}</span>
                  </div>
                )}
              </div>
              <div className="auth-field">
                <label className="auth-label">{t("auth.confirmPassword")}</label>
                <div className="auth-input-wrap">
                  <i className="fa-solid fa-lock auth-input-icon"></i>
                  <input type="password" name="confirm" className="auth-input" placeholder={t("auth.confirmPasswordPlaceholder")} value={form.confirm} onChange={handleChange} required />
                  {form.confirm && (
                    <span className="auth-eye-btn" style={{ pointerEvents: "none" }}>
                      <i className={`fa-solid ${form.password === form.confirm ? "fa-check" : "fa-xmark"}`} style={{ color: form.password === form.confirm ? "#10b981" : "#ef4444" }}></i>
                    </span>
                  )}
                </div>
              </div>
              <button type="submit" className="auth-submit" disabled={loading}>
                {loading ? <><span className="auth-spinner"></span> {t("auth.creating")}</> : <>{t("auth.createAccount")} <i className="fa-solid fa-arrow-right-long"></i></>}
              </button>
              <div className="auth-or"><span>{t("auth.orContinue")}</span></div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <GoogleLogin onSuccess={handleGoogleSuccess} onError={handleGoogleError} useOneTap shape="rectangular" theme="filled_black" size="large" text="continue_with" width="100%" />
              </div>
            </form>
            <p className="auth-switch">{t("auth.alreadyHave")} <Link to="/login" className="auth-switch-link">{t("auth.signInInstead")}</Link></p>
            <div className="auth-card-footer"><i className="fa-solid fa-shield-halved"></i> Protected by JWT · Solviotech Admin © {new Date().getFullYear()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
